import React from 'react';
import { Pencil, Move, Maximize2, Trash2, Plus } from 'lucide-react';
import { useWordDataContext } from '../contexts/WordDataContext';
import type { ApprovalData } from './SpreadEditor';

interface TopEditorProps {
  approval?: ApprovalData;
  currentApproval?: ApprovalData;
  onApprovalChange: (approval: ApprovalData) => void;
  onNotesChange: (notes: string) => void;
  onSave: () => void;
  hasChanges: boolean;
  isSaving: boolean;
  saveButtonText: string;
  saveButtonTitle: string;
  onAddBlockLeft?: () => void;
  onAddBlockRight?: () => void;
}

const TopEditor: React.FC<TopEditorProps> = ({
  approval,
  currentApproval,
  onApprovalChange,
  onNotesChange,
  onSave,
  hasChanges,
  isSaving,
  saveButtonText,
  saveButtonTitle,
  onAddBlockLeft,
  onAddBlockRight,
}) => {
  const { state: wordDataState, actions } = useWordDataContext();

  // Get approval status
  const getImagesStatus = () => {
    const value = currentApproval?.images;
    if (value === true || value === 'true') return 'approved';
    if (value === false || value === 'false') return 'fail';
    if (value === 'auto') return 'auto';
    return 'unapproved';
  };

  const getWordsStatus = () => {
    const value = currentApproval?.words;
    if (value === true || value === 'true') return 'approved';
    if (value === false || value === 'false') return 'fail';
    if (value === 'auto') return 'auto';
    return 'unapproved';
  };

  const getVoiceStatus = () => {
    const value = currentApproval?.voice;
    if (value === true) return 'added';
    return 'not-chosen';
  };

  const getNextImagesValue = () => {
    const current = currentApproval?.images;
    if (current === true || current === 'true') return false;
    if (current === false || current === 'false') return undefined;
    return true; // unapproved or auto -> approved
  };

  const getNextWordsValue = () => {
    const current = currentApproval?.words;
    if (current === true) return false;
    if (current === false) return undefined;
    return true;
  };

  const getNextVoiceValue = () => {
    const current = currentApproval?.voice;
    if (current === true) return undefined;
    return true;
  };

  const handleApprovalClick = (type: 'images' | 'words' | 'voice', value: boolean | undefined) => {
    const updatedApproval = {
      ...currentApproval,
      [type]: value,
    };
    onApprovalChange(updatedApproval);
  };

  const handleSetEditMode = (mode: 'text' | 'resize' | 'move' | 'delete-word' | 'delete-block' | null) => {
    actions.setEditMode(mode);
  };

  const imagesStatus = getImagesStatus();
  const wordsStatus = getWordsStatus();
  const voiceStatus = getVoiceStatus();

  // Base button style shared by all buttons
  const baseButtonStyle: React.CSSProperties = {
    border: '1px solid var(--border-primary)',
    borderRadius: '4px',
    fontSize: '11px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
  };

  // Helper function to create button style with overrides
  const getButtonStyle = (overrides?: React.CSSProperties): React.CSSProperties => {
    return { ...baseButtonStyle, ...overrides };
  };

  return (
    <div className="top-editor" style={{ 
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '0 8px',
      height: '50px',
      width: '100%',
      overflowX: 'auto',
      overflowY: 'hidden',
    }}>
      {/* Approval Buttons */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', paddingRight: '8px' }}>
        <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', marginRight: '4px' }}>Book:</span>
        {/* Images */}
        <button
          className={`approval-btn images ${imagesStatus}`}
          onClick={() => handleApprovalClick('images', getNextImagesValue())}
          title={`Images: ${imagesStatus === 'approved' ? 'Approved' : imagesStatus === 'fail' ? 'Failed' : imagesStatus === 'auto' ? 'Auto' : 'Unapproved'}`}
          style={getButtonStyle()}
        >
          {imagesStatus === 'approved' ? '🖼️✅' : imagesStatus === 'fail' ? '🖼️❌' : imagesStatus === 'auto' ? '🖼️✓' : '🖼️'} {imagesStatus === 'approved' ? 'Approved' : imagesStatus === 'fail' ? 'Fail' : imagesStatus === 'auto' ? 'Auto' : 'Unapproved'}
        </button>

        {/* Words */}
        <button
          className={`approval-btn words ${wordsStatus}`}
          onClick={() => handleApprovalClick('words', getNextWordsValue())}
          title={`Words: ${wordsStatus === 'approved' ? 'Approved' : wordsStatus === 'fail' ? 'Failed' : wordsStatus === 'auto' ? 'Auto' : 'Unapproved'}`}
          style={getButtonStyle()}
        >
          {wordsStatus === 'approved' ? '📝✅' : wordsStatus === 'fail' ? '📝❌' : wordsStatus === 'auto' ? '📝✓' : '📝'} {wordsStatus === 'approved' ? 'Approved' : wordsStatus === 'fail' ? 'Fail' : wordsStatus === 'auto' ? 'Auto' : 'Unapproved'}
        </button>

        {/* Voice */}
        <button
          className={`approval-btn voice ${voiceStatus}`}
          onClick={() => handleApprovalClick('voice', getNextVoiceValue())}
          title={`Voice: ${voiceStatus === 'added' ? 'Added' : 'Not chosen'}`}
          style={getButtonStyle()}
        >
          {voiceStatus === 'added' ? '🎤✅' : '🎤'} {voiceStatus === 'added' ? 'Added' : 'Not chosen'}
        </button>
      </div>

      {/* Vertical Divider */}
      <div style={{ 
        width: '1px', 
        height: '30px', 
        backgroundColor: 'var(--border-primary)',
        margin: '0 4px'
      }} />

      {/* Save Button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', paddingRight: '8px' }}>
        <span style={{ fontSize: '18px', lineHeight: '28px', display: 'flex', alignItems: 'center' }}>💾</span>
        <button
          className="btn-save"
          onClick={onSave}
          disabled={!hasChanges || isSaving}
          style={getButtonStyle({
            height: '28px',
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: '500',
            minWidth: '110px',
            backgroundColor: hasChanges ? '#dc2626' : 'var(--bg-secondary)',
            border: hasChanges ? '1px solid #dc2626' : '1px solid var(--border-primary)',
            color: hasChanges ? 'white' : 'var(--text-secondary)',
            cursor: hasChanges && !isSaving ? 'pointer' : 'not-allowed',
            opacity: hasChanges && !isSaving ? 1 : 0.85,
          })}
          title={saveButtonTitle}
        >
          {saveButtonText}
        </button>
      </div>

      {/* Vertical Divider */}
      <div style={{ 
        width: '1px', 
        height: '30px', 
        backgroundColor: 'var(--border-primary)',
        margin: '0 4px'
      }} />

      {/* Edit Mode Buttons */}
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className={`edit-mode-btn ${wordDataState.editMode === 'text' ? 'active' : ''}`}
          onClick={() => handleSetEditMode(wordDataState.editMode === 'text' ? null : 'text')}
          title="Edit text - Click on words to edit their text"
          style={getButtonStyle({
            border: `1px solid ${wordDataState.editMode === 'text' ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
            backgroundColor: wordDataState.editMode === 'text' ? 'var(--accent-primary)' : 'var(--bg-secondary)',
            color: wordDataState.editMode === 'text' ? 'white' : 'var(--text-primary)',
            gap: '4px',
            justifyContent: 'flex-start',
          })}
        >
          <Pencil size={14} />
          <span>Text</span>
        </button>
        
        <button
          className={`edit-mode-btn ${wordDataState.editMode === 'resize' ? 'active' : ''}`}
          onClick={() => handleSetEditMode(wordDataState.editMode === 'resize' ? null : 'resize')}
          title="Resize - Hover over word edges/corners and drag to resize"
          style={getButtonStyle({
            border: `1px solid ${wordDataState.editMode === 'resize' ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
            backgroundColor: wordDataState.editMode === 'resize' ? 'var(--accent-primary)' : 'var(--bg-secondary)',
            color: wordDataState.editMode === 'resize' ? 'white' : 'var(--text-primary)',
            gap: '4px',
            justifyContent: 'flex-start',
          })}
        >
          <Maximize2 size={14} />
          <span>Resize</span>
        </button>
        
        <button
          className={`edit-mode-btn ${wordDataState.editMode === 'move' ? 'active' : ''}`}
          onClick={() => handleSetEditMode(wordDataState.editMode === 'move' ? null : 'move')}
          title="Move - Click and drag words to move them"
          style={getButtonStyle({
            border: `1px solid ${wordDataState.editMode === 'move' ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
            backgroundColor: wordDataState.editMode === 'move' ? 'var(--accent-primary)' : 'var(--bg-secondary)',
            color: wordDataState.editMode === 'move' ? 'white' : 'var(--text-primary)',
            gap: '4px',
            justifyContent: 'flex-start',
          })}
        >
          <Move size={14} />
          <span>Move</span>
        </button>
        
        <button
          className={`edit-mode-btn ${wordDataState.editMode === 'delete-word' ? 'active' : ''}`}
          onClick={() => handleSetEditMode(wordDataState.editMode === 'delete-word' ? null : 'delete-word')}
          title="Delete word - Click on words to delete them"
          style={getButtonStyle({
            border: `1px solid ${wordDataState.editMode === 'delete-word' ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
            backgroundColor: wordDataState.editMode === 'delete-word' ? 'var(--accent-primary)' : 'var(--bg-secondary)',
            color: wordDataState.editMode === 'delete-word' ? 'white' : 'var(--text-primary)',
            gap: '4px',
            justifyContent: 'flex-start',
          })}
        >
          <Trash2 size={14} />
          <span>Del Word</span>
        </button>
        
        <button
          className={`edit-mode-btn ${wordDataState.editMode === 'delete-block' ? 'active' : ''}`}
          onClick={() => handleSetEditMode(wordDataState.editMode === 'delete-block' ? null : 'delete-block')}
          title="Delete block - Click on blocks to delete them (with confirmation)"
          style={getButtonStyle({
            border: `1px solid ${wordDataState.editMode === 'delete-block' ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
            backgroundColor: wordDataState.editMode === 'delete-block' ? 'var(--accent-primary)' : 'var(--bg-secondary)',
            color: wordDataState.editMode === 'delete-block' ? 'white' : 'var(--text-primary)',
            gap: '4px',
            justifyContent: 'flex-start',
          })}
        >
          <Trash2 size={14} />
          <span>Del Block</span>
        </button>
        
        {onAddBlockLeft && (
          <button
            onClick={onAddBlockLeft}
            title="Add block to left page"
            style={getButtonStyle({
              border: '1px solid var(--border-primary)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              gap: '4px',
              justifyContent: 'flex-start',
            })}
          >
            <span>L<Plus size={10} /></span>
          </button>
        )}
        
        {onAddBlockRight && (
          <button
            onClick={onAddBlockRight}
            title="Add block to right page"
            style={getButtonStyle({
              border: '1px solid var(--border-primary)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              gap: '4px',
              justifyContent: 'flex-start',
            })}
          >
            <span>R<Plus size={10} /></span>
          </button>
        )}
      </div>

    </div>
  );
};

export default TopEditor;

